import type { ParcoursId } from './useOnboardingProgress';

/**
 * Source unique d'état de l'onboarding (LOT 2). Avant ce lot, deux
 * composants (`OnboardingPage` et `OnboardingBanner`) écrivaient
 * indépendamment les mêmes clés `localStorage`. Ce module est désormais le
 * seul point d'écriture/lecture : `OnboardingPage` est le seul consommateur
 * en écriture, les autres pages ne font que lire via les fonctions
 * exportées ici (ou via `useOnboardingParcours`, qui lit la même clé
 * `parcours`, inchangée pour ne pas casser ses 3 consommateurs existants).
 */

const KEYS = {
  parcours: 'ix_onboarding_parcours',
  dismissed: 'ix_onboarding_dismissed',
} as const;

/**
 * `completed` : l'utilisateur a terminé l'onboarding (bouton "Continuer").
 * `deferred` : l'utilisateur a choisi "Plus tard" — l'app ne doit plus le
 * bloquer, mais une reprise doit rester proposée (Accueil/Profil).
 */
export type OnboardingCompletion = 'completed' | 'deferred';

/**
 * Lit l'état de complétion en tenant compte des anciennes valeurs
 * (Phase I — migration) :
 * - `dismissed === '1'` (ancien format, unique valeur possible avant ce lot)
 *   → interprété comme `completed`, jamais comme `deferred` : un utilisateur
 *   déjà onboardé ne doit pas se voir proposer une reprise qu'il n'a jamais
 *   demandée.
 * - `parcours` renseigné mais `dismissed` absent (l'utilisateur avait choisi
 *   un parcours puis fermé l'app avant la fin de l'ancien flux) → également
 *   interprété comme `completed`, à l'identique du comportement de l'ancien
 *   code (`alreadyOnboarded = Boolean(parcours) || dismissed === '1'`).
 */
export function readOnboardingCompletion(): OnboardingCompletion | null {
  const raw = localStorage.getItem(KEYS.dismissed);
  if (raw === 'deferred') return 'deferred';
  if (raw === 'completed' || raw === '1') return 'completed';
  if (localStorage.getItem(KEYS.parcours)) return 'completed';
  return null;
}

export function isOnboardingResolved(): boolean {
  return readOnboardingCompletion() !== null;
}

export function isOnboardingDeferred(): boolean {
  return readOnboardingCompletion() === 'deferred';
}

export function markOnboardingCompleted(): void {
  localStorage.setItem(KEYS.dismissed, 'completed');
}

export function markOnboardingDeferred(): void {
  localStorage.setItem(KEYS.dismissed, 'deferred');
}

export function setOnboardingParcours(id: ParcoursId): void {
  localStorage.setItem(KEYS.parcours, id);
  window.dispatchEvent(new CustomEvent('ix:parcours-changed'));
}
